// Dashboard visual token system.
//
// One source of truth for the admin Dashboard's card palette. Consumed
// by DashboardTintedCard + directly by the FinanceOverview /
// OperationsCommandCenter / DailyBriefingBar files so per-page hex
// values stop drifting.
//
// 2026-09 Full-Fill Rainbow pass (v2 correction): the earlier "very
// pale pastel + decorative bloom circle" iteration read as almost-
// white cards. This revision uses stronger soft-to-medium saturated
// solid fills so every card is CLEARLY the colour it represents.
//
// Every colour surface in this file is a SOLID hex. No linear-,
// radial-, or conic-gradient values are allowed anywhere in the
// dashboard tokens or their consumers. No translucent decorative
// overlays / bloom circles / faded blobs / glossy shapes either — a
// card is exactly its `grad` colour edge-to-edge.
//
// Token object shape (field names, keys, types) preserved from the
// previous revision so no consumer file has to change to pick up the
// new palette. `toneMap[t].grad` carries a solid pastel hex — same
// field name, same downstream `background: t.grad` usage.

export const dashTokens = {
  color: {
    // Brand anchors — deep navy retained as the strongest visual
    // anchor (used by the two hero bars); the rest of the palette
    // is now rainbow-driven via the toneMap below.
    navy:        "#163B63",
    midnight:    "#0F2440",
    deepGreen:   "#163B63",   // legacy alias → brand navy
    // Semantic status accents (used inline in a handful of places
    // outside the toneMap). Kept vivid but SaaS-appropriate.
    emerald:     "#10B981",
    financeGreen:"#10B981",   // legacy alias → emerald accent
    royalBlue:   "#2563EB",
    skyBlue:     "#D9F3FF",
    purple:      "#9333EA",
    lavender:    "#E9DDFB",
    amber:       "#D97706",
    amberAccent: "#D97706",
    coral:       "#F97316",
    red:         "#F43F5E",
    teal:        "#0F766E",
    // Text tokens (spec §"TEXT CONTRAST"). Higher-contrast primary
    // than the previous pass so dark text over medium pastel fills
    // stays crisp.
    text:        "#102A43",
    muted:       "#64748B",
    disabled:    "#94A3B8",
    // Structural neutrals
    border:      "rgba(16,42,67,0.08)",
    borderStrong:"rgba(16,42,67,0.14)",
    card:        "#FFFFFF",
    // Page background — light neutral so colourful cards stand out.
    pageBg:      "#F4F7FB",
  },
  radius: {
    sm: 10,
    md: 14,
    lg: 18,
    xl: 22,
  },
  shadow: {
    card:      "0 6px 18px rgba(16,42,67,0.06)",
    cardHover: "0 10px 24px rgba(16,42,67,0.10)",
    hero:      "0 12px 32px rgba(16,42,67,0.16)",
  },
  transition: "all 180ms cubic-bezier(0.2, 0.8, 0.2, 1)",
} as const;

// Rainbow tone union. Legacy tone names (blue/green/teal/purple/amber/
// red/navy/mint) are preserved so existing consumers keep compiling;
// the new sky/cyan/emerald/lime/orange/rose/pink/violet/indigo tones
// give per-card identity to Finance / Operations / Quick Action rows
// without adding any new component call sites.
export type Tone =
  | "blue" | "sky" | "cyan" | "teal"
  | "emerald" | "green" | "lime" | "mint" | "yellow"
  | "amber" | "orange"
  | "coral" | "red" | "rose" | "pink"
  | "purple" | "violet" | "indigo"
  | "navy";

/**
 * Tone → colour set used by DashboardTintedCard. Each tone provides:
 *   grad     — SOLID medium-saturation background hex. Fills the
 *              entire card edge-to-edge; no overlays, no gradients.
 *              (Field name kept for backwards compat with existing
 *              `background: t.grad` consumers.)
 *   iconBg   — deeper solid for the icon tile / accent stripes
 *   iconFg   — icon color inside the tile
 *   accent   — deep text/badge accent for values (used for numbers,
 *              headings — spec §"TEXT CONTRAST")
 *   border   — solid slightly-deeper matching border
 */
export const toneMap: Record<Tone, {
  grad: string; iconBg: string; iconFg: string; accent: string; border: string;
}> = {
  // Cool blues
  blue: {
    grad:   "#DCEBFF",
    iconBg: "#2563EB", iconFg: "#FFFFFF",
    accent: "#1E3A8A", border: "#9CC2FF",
  },
  sky: {
    grad:   "#D9F3FF",
    iconBg: "#0284C7", iconFg: "#FFFFFF",
    accent: "#0C4A6E", border: "#97DFFF",
  },
  cyan: {
    grad:   "#D7F7FF",
    iconBg: "#0891B2", iconFg: "#FFFFFF",
    accent: "#155E75", border: "#8EEBFF",
  },
  teal: {
    grad:   "#D8FAF4",
    iconBg: "#0F766E", iconFg: "#FFFFFF",
    accent: "#0F5D57", border: "#99EBDD",
  },
  // Greens
  emerald: {
    grad:   "#DCFCEB",
    iconBg: "#10B981", iconFg: "#FFFFFF",
    accent: "#065F46", border: "#9AE6BE",
  },
  green: {
    grad:   "#E3F9D6",
    iconBg: "#65A30D", iconFg: "#FFFFFF",
    accent: "#3F6212", border: "#B7E48A",
  },
  lime: {
    grad:   "#ECFCCB",
    iconBg: "#84CC16", iconFg: "#FFFFFF",
    accent: "#3F6212", border: "#D9F99D",
  },
  mint: {
    grad:   "#DCFCEB",
    iconBg: "#10B981", iconFg: "#FFFFFF",
    accent: "#065F46", border: "#9AE6BE",
  },
  yellow: {
    grad:   "#FEF3C7",
    iconBg: "#CA8A04", iconFg: "#FFFFFF",
    accent: "#713F12", border: "#FCD34D",
  },
  // Warms
  amber: {
    grad:   "#FDE7B0",
    iconBg: "#D97706", iconFg: "#FFFFFF",
    accent: "#78350F", border: "#F6C453",
  },
  orange: {
    grad:   "#FED7AA",
    iconBg: "#EA580C", iconFg: "#FFFFFF",
    accent: "#7C2D12", border: "#FDBA74",
  },
  // Reds / rose / pink
  coral: {
    grad:   "#FFD9CC",
    iconBg: "#F97316", iconFg: "#FFFFFF",
    accent: "#9A3412", border: "#FFB39A",
  },
  red: {
    grad:   "#FFE0E6",
    iconBg: "#EF4444", iconFg: "#FFFFFF",
    accent: "#7F1D1D", border: "#FFB3C1",
  },
  rose: {
    grad:   "#FFE0E6",
    iconBg: "#F43F5E", iconFg: "#FFFFFF",
    accent: "#881337", border: "#FFB3C1",
  },
  pink: {
    grad:   "#FCE7F3",
    iconBg: "#DB2777", iconFg: "#FFFFFF",
    accent: "#831843", border: "#F9A8D4",
  },
  // Purples
  purple: {
    grad:   "#E9DDFB",
    iconBg: "#9333EA", iconFg: "#FFFFFF",
    accent: "#4C1D95", border: "#CDB4F6",
  },
  violet: {
    grad:   "#EDE9FE",
    iconBg: "#7C3AED", iconFg: "#FFFFFF",
    accent: "#4C1D95", border: "#C4B5FD",
  },
  indigo: {
    grad:   "#E0E7FF",
    iconBg: "#4F46E5", iconFg: "#FFFFFF",
    accent: "#312E81", border: "#A5B4FC",
  },
  // Navy anchor — kept for status cards / secondary heroes. Uses a
  // deeper slate fill so cards using tone="navy" read as clearly
  // navy-toned rather than almost-grey.
  navy: {
    grad:   "#DDE6F3",
    iconBg: "#163B63", iconFg: "#FFFFFF",
    accent: "#0F2440", border: "#A6BAD3",
  },
};

/**
 * Stable location-index → tone rotation. Ajax gets sky-blue and
 * Scarborough gets violet per spec §"REVENUE BY LOCATION" so the two
 * location cards carry consistent identities everywhere they render
 * (Finance revenue tiles + Operations "Locations Today").
 * A single-tenant install with more than two locations rotates
 * through cyan / emerald / amber before wrapping.
 */
export const LOCATION_TONE_ROTATION: Tone[] = ["sky", "violet", "cyan", "emerald", "amber"];

export const toneForLocationIndex = (i: number): Tone =>
  LOCATION_TONE_ROTATION[i % LOCATION_TONE_ROTATION.length];
